import { useEffect, useState, useCallback, memo } from 'react';
import { API_BASE } from '../api/client';
import axios from 'axios';
import useUser from '../hooks/useUser';
import {
  Box,
  Paper,
  Typography,
  Divider,
  TextField,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// --- Constants ---
const REQUEST_FIELDS = {
  horizontal: [
    'ObjectID', 'GPS', 'NGSPID', 'Mslink', 'OCRTN', 'SoCalGasArea',
    'Revised', 'Destroyed',
    'Location', 'Benchmark', 'Reference', 'CornerRecord',
    'ThomasBrosRef', 'MapIndexRef', 'Remarks', 'Description',
    'ConvergenceAngle',
    'ScaleFactor', 'EllipsoidFactor', 'ComboFactor',
    'Latitude1991', 'Longitude1991', 'Northing1991Ft', 'Easting1991Ft', 'EllipsoidHeight1991Ft',
    'Latitude2007', 'Longitude2007', 'Northing2007FT', 'Easting2007FT', 'EllipsoidHeight2007FT',
    'Latitude2017', 'Longitude2017', 'Northing2017', 'Easting2017', 'EllipsoidHeight2017Ft',
    'ScaleFactor2017', 'EllipsoidFactor2017', 'ComboFactor2017',
  ],
  vertical: [
    'ObjectID', 'Designation', 'Revised', 'Destroyed',
    'Latitude', 'Longitude',
    'YearLeveledRow1','YearLeveledRow2','YearLeveledRow3','YearLeveledRow4','YearLeveledRow5',
    'NAV88Row1','NAV88Row2','NAV88Row3','NAV88Row4','NAV88Row5',
    'NGVD29Row1','NGVD29Row2','NGVD29Row3','NGVD29Row4','NGVD29Row5',
    'GPS','OCSIndex','PageGrid','Location','DateDestroyed','LineSSN','Description','EstablishedBy'
  ],
};

const BOX_FOLDER_IDS = {
  horizontal: { itemImage: '336014142295', mapImage: '336014139895' },
  vertical: { itemImage: '336014946062', mapImage: '336014948462' }
};

const fmt = (v) => (v == null ? '' : String(v));
const fmtDateMs = (ms) => (ms ? new Date(ms).toISOString().slice(0, 10) : '');

// --- Styled Components ---
const ScrollBox = styled(Box)({
  maxHeight: 600,
  overflowY: 'auto',
  paddingRight: 8,
});

const SectionTitle = styled(Typography)({
  fontWeight: 600,
  marginTop: 24,
  marginBottom: 8,
  fontSize: '1rem',
  letterSpacing: '0.02em',
});

const FieldLabel = styled('span')(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.secondary,
  fontSize: '0.95em',
}));

// --- Memoized Field ---
const Field = memo(function Field({ label, name, value, onChange, helperText, ...rest }) {
  const isDescription = (name ?? "").toLowerCase() === "description";
  const MAX_DESC_LEN = 1000;
  return (
    <TextField
      id={name}
      label={<FieldLabel>{label}</FieldLabel>}
      name={name}
      value={value}
      onChange={onChange}
      fullWidth
      size="small"
      multiline={isDescription}
      minRows={isDescription ? 6 : undefined}
      maxRows={isDescription ? 12 : undefined}
      inputProps={isDescription ? { maxLength: MAX_DESC_LEN } : undefined}
      helperText={isDescription ? `${value.length}/${MAX_DESC_LEN}` : (helperText ?? "")}
      FormHelperTextProps={isDescription ? { sx: { textAlign: "right" } } : undefined}
      onKeyDown={
        isDescription
          ? (e) => { if (e.key === "Enter") e.stopPropagation(); }
          : undefined
      }
      sx={isDescription ? { gridColumn: "1 / -1", "& .MuiInputBase-inputMultiline": { resize: "vertical" } } : undefined}
      {...rest}
    />
  );
});

/* ==============================
   Image preview box (image/** or PDF)
   ============================== */
let imageFileId;
let pdfFileId;

const ImageBox = ({ title, fileId, imageType, formType, onResolved }) => {
  const [src, setSrc] = useState(null);
  const [mime, setMime] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let url;
    let cancelled = false;

    (async () => {
      setSrc(null);
      setMime(null);

      if (!fileId) return;

      try {
        let folderId;
        if(formType === 'horizontal'){
          if(imageType === 'itemImage'){
            folderId = BOX_FOLDER_IDS.horizontal.itemImage;
          }else{
            folderId = BOX_FOLDER_IDS.horizontal.mapImage;
          }
        }
        else{
          if(imageType === 'itemImage'){
            folderId = BOX_FOLDER_IDS.vertical.itemImage;
          }else{
            folderId = BOX_FOLDER_IDS.vertical.mapImage;
          }
        }

        const searchResp = await fetch(
          `${API_BASE}/api/box/v1/files?search=${encodeURIComponent(fileId)}&folder=${folderId}`
        );
        if (!searchResp.ok) throw new Error(`HTTP ${searchResp.status}`);

        const data = await searchResp.json();
        const firstFileId = data.items?.[0]?.id;
        if (!firstFileId) return;
        if(imageType === 'itemImage'){
          imageFileId = firstFileId
        }else{
          pdfFileId = firstFileId
        }

        const streamResp = await fetch(`${API_BASE}/api/box/v1/files/${firstFileId}/stream`);
        if (!streamResp.ok) throw new Error(`HTTP ${streamResp.status}`);

        const contentType = (streamResp.headers.get('Content-Type') || '').toLowerCase();
        const blob = await streamResp.blob();
        url = URL.createObjectURL(blob);

        if (!cancelled) {
          setSrc(url);
          setMime(contentType);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('ImageBox load error:', e);
          setSrc(null);
          setMime(null);
        }
      }
    })();

    return () => {
      if (url) URL.revokeObjectURL(url);
      cancelled = true;
    };
  }, [fileId, imageType, formType]);

  const renderPreview = () => {
    if (!src) {
      return <Typography variant="body2" color="text.secondary">No image</Typography>;
    }
    if (mime?.startsWith('image/')) {
      return (
        <img
          alt={title}
          src={src}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      );
    }
    if (mime === 'application/pdf' || mime?.includes('pdf')) {
      return (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.selected',
            color: 'text.secondary',
            fontSize: 14,
            textAlign: 'center',
            px: 2,
          }}
        >
          Click to view PDF
        </Box>
      );
    }
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Unsupported preview ({mime || 'unknown type'})
        </Typography>
        <Button component="a" href={src} download>
          Download
        </Button>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
      <Box
        onClick={() => src && setOpen(true)}
        sx={{
          height: 220,
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'action.hover',
          display: 'grid',
          placeItems: 'center',
          cursor: src ? 'zoom-in' : 'default',
        }}
      >
        {renderPreview()}
      </Box>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          {src && mime?.startsWith('image/') && (
            <img alt={title} src={src} style={{ width: '100%', height: 'auto' }} />
          )}
          {src && (mime === 'application/pdf' || mime?.includes('pdf')) && (
            <iframe
              title={title}
              src={src + '#toolbar=1&view=FitH'}
              style={{ width: '100%', height: '80vh', border: 'none' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const PreviewPanel = ({ fields, mapId, formType }) => (
  <Box sx={{ position: { lg: 'sticky' }, top: { lg: 88 }, display: 'grid', gap: 2 }}>
    <Typography variant="h6">Preview</Typography>
    <ImageBox title="Monument Photo" fileId={fields?.gps} imageType="itemImage" formType={formType} />
    <ImageBox
      title={`Map Image${mapId ? ` — GPS ${mapId}` : ''}`}
      fileId={fields?.gps}
      imageType="mapImage"
      formType={formType}
    />
  </Box>
);

const HorizontalVerticalControlForm = () => {
  // --- UI state ---
  const [formType, setFormType] = useState('horizontal');
  const [mapId, setMapId] = useState('');
  const [fields, setFields] = useState(() => Object.fromEntries((REQUEST_FIELDS['horizontal'] ?? []).map(k => [k, ''])));
  const [lookupLoading, setLookupLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user } = useUser();

  // --- Helpers ---
  const blankFields = useCallback(
    (type) => Object.fromEntries((REQUEST_FIELDS[type] ?? []).map(k => [k, ''])),
    []
  );

  // --- Lookup handler ---
  const handleLookup = useCallback(async () => {
    setError('');
    setSuccess('');
    if (!mapId.trim()) {
      setError('Enter a GPS name to search');
      return;
    }
    setLookupLoading(true);
    try {
      const token = user && (await user.getIdToken());
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      let url, params;
      if (formType === 'horizontal') {
        url = `${API_BASE}/api/oc-survey/v1/geodetic/horizontal`;
        params = { gpsnumber: mapId.trim() };
      } else {
        url = `${API_BASE}/api/oc-survey/v1/geodetic/vertical`;
        params = { designation: mapId.trim() };
      }
      const { data } = await axios.get(url, { params, headers });
      const allow = new Set(REQUEST_FIELDS[formType]);
      const next = blankFields(formType);
      for (const k of allow) {
        if (data[k] !== undefined) {
          if (k === 'revised' || k === 'destroyed') next[k] = fmtDateMs(data[k]);
          else next[k] = fmt(data[k]);
        }
      }
      if (!next.gps) next.gps = mapId.trim();
      setFields(next);
      setSuccess('Metadata loaded. Review the fields below before submitting.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Unable to fetch metadata for that GPS name.');
    } finally {
      setLookupLoading(false);
    }
  }, [formType, mapId, user, blankFields]);

  // --- Field change handler ---
  const handleFieldChange = useCallback((key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  }, []);

  // --- Submit handler ---
  const buildPayload = useCallback(() => {
    const allow = new Set([...(formType === 'horizontal' ? REQUEST_FIELDS.horizontal : REQUEST_FIELDS.vertical)]);
    const filtered = Object.fromEntries(Object.entries(fields).filter(([k]) => allow.has(k)));
    if ('Revised' in filtered) filtered.Revised = filtered.revised ? Number(filtered.revised) : null;
    if ('Destroyed' in filtered) filtered.Destroyed = filtered.destroyed ? Number(filtered.destroyed) : null;
    if (formType === 'horizontal') {
      var gpsNumValue = mapId === fields.GPS ? mapId : fields.GPS; 
      return {
        ...filtered,
        formType,
        GPSNum: gpsNumValue.trim(),
        GeodeticType: formType,
        ImageBoxId: imageFileId,
        PdfBoxId: pdfFileId,
        description: fields.Description || fields.reference || '',
        FeatureObjectId: fields.ObjectID || null,
        OriginalGPSNum: mapId.trim(),
      };
    } else {
      var designationValue = mapId === fields.Designation ? mapId : fields.Designation;
      return {
        ...filtered,
        formType,
        Designation: designationValue.trim(),
        MonumentType: formType,
        ImageBoxId: imageFileId,
        PdfBoxId: pdfFileId,
        description: fields.Description || fields.reference || '',
        FeatureObjectId: fields.ObjectID || null,
        OriginalDesignation: mapId.trim()
      };
    }
  }, [fields, formType, mapId]);

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();
    setError('');
    setSuccess('');
    if (!user) {
      setError('You must be logged in to submit.');
      return;
    }
    if (!mapId.trim()) {
      setError('Map ID (GPS) is required.');
      return;
    }
    setSubmitLoading(true);
    try {
      const token = user && (await user.getIdToken());
      const headers = token ? { Authorization: `Bearer ${token}`, Accept: 'application/pdf' } : { Accept: 'application/pdf' };
      const payload = buildPayload();
      const response = await axios.post(
        `${API_BASE}/api/oc-survey/v1/${payload.formType}`,
        payload,
        { responseType: 'blob', headers }
      );
      const contentType = response.headers["content-type"] || "";
      if (!contentType.includes("application/pdf")) {
        const msg = await response.data.text?.() ?? "Failed to generate PDF.";
        throw new Error(msg);
      }
      const cd = response.headers["content-disposition"] || "";
      const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
      const fileName = (match && decodeURIComponent(match[1])) || `${mapId}.pdf`;
      const blob = new Blob([response.data], { type: "application/pdf" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      setSuccess('Submitted successfully.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  }, [user, mapId, buildPayload]);

  // --- Group fields for readability ---
  const fieldGroups = {
    General: ['ObjectID', 'GPS', 'NGSPID', 'Mslink', 'OCRTN', 'SoCalGasArea', 'Designation'],
    Status: ['Revised', 'Destroyed', 'DateDestroyed'],
    Location: ['Location', 'Benchmark', 'Reference', 'CornerRecord', 'ThomasBrosRef', 'MapIndexRef', 'Remarks', 'Description'],
    Factors: [
      'ConvergenceAngle', 'ScaleFactor', 'EllipsoidFactor', 'ComboFactor',
      'ScaleFactor2017', 'EllipsoidFactor2017', 'ComboFactor2017'
    ],
    Coordinates: [
      'Latitude1991', 'Longitude1991', 'Northing1991Ft', 'Easting1991Ft', 'EllipsoidHeight1991Ft',
      'Latitude2007', 'Longitude2007', 'Northing2007FT', 'Easting2007FT', 'EllipsoidHeight2007FT',
      'Latitude2017', 'Longitude2017', 'Northing2017', 'Easting2017', 'EllipsoidHeight2017Ft'
    ],
    Vertical: [
      'YearLeveledRow1','YearLeveledRow2','YearLeveledRow3','YearLeveledRow4','YearLeveledRow5',
      'NAV88Row1','NAV88Row2','NAV88Row3','NAV88Row4','NAV88Row5',
      'NGVD29Row1','NGVD29Row2','NGVD29Row3','NGVD29Row4','NGVD29Row5',
      'GPS','OCSIndex','PageGrid','LineSSN','EstablishedBy'
    ]
  };

  // --- Render ---
  return (
    <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        {formType === 'horizontal' ? 'Add Horizontal Control' : 'Add Vertical Control'}
      </Typography>

      {/* Auth status / prompts */}
      {user ? (
        <Typography variant="body2" sx={{ mb: 2 }}>
          Logged in as <strong>{user.email}</strong>
        </Typography>
      ) : (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You must be logged in to submit data.
        </Alert>
      )}

      {/* Inline status messaging */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' } }}>
        {/* LEFT COLUMN — Form */}
        <ScrollBox>
          {/* Form type toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2">Form:</Typography>
            <ToggleButtonGroup
              size="small"
              color="primary"
              exclusive
              value={formType}
              onChange={(e, next) => {
                if (!next) return;
                setFormType(next);
                setFields(blankFields(next));
                setMapId('');
                setError('');
                setSuccess('');
              }}
            >
              <ToggleButton value="horizontal">Horizontal</ToggleButton>
              <ToggleButton value="vertical">Vertical</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Search by GPS / Map ID */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
            <TextField
              label="GPS / Map ID"
              value={mapId}
              onChange={(e) => setMapId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleLookup();
                }
              }}
              required
            />
            <Button
              onClick={handleLookup}
              variant="outlined"
              disabled={lookupLoading || !mapId.trim()}
              startIcon={lookupLoading ? <CircularProgress size={18} /> : null}
            >
              {lookupLoading ? 'Fetching…' : 'Fetch Metadata'}
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Metadata fields grid, grouped */}
          {Object.entries(fieldGroups).map(([group, keys]) => {
            if (formType === 'horizontal' && group === 'Vertical') return null;
            if (formType === 'vertical' && group === 'Coordinates') return null;
            return (
              <Box key={group} sx={{ mb: 2 }}>
                <SectionTitle>{group}</SectionTitle>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                  }}
                >
                  {keys.filter(apiKey => REQUEST_FIELDS[formType].includes(apiKey)).map((apiKey) => {
                    const isDesc = apiKey.toLowerCase() === "description";
                    const val = fields[apiKey] ?? "";
                    const MAX = 1000;
                    return (
                      <Field
                        key={`${formType}-${apiKey}`}
                        label={apiKey}
                        name={apiKey}
                        value={val}
                        onChange={(e) => handleFieldChange(apiKey, e.target.value)}
                        multiline={isDesc}
                        minRows={isDesc ? 6 : undefined}
                        maxRows={isDesc ? 12 : undefined}
                        inputProps={isDesc ? { maxLength: MAX } : undefined}
                        helperText={isDesc ? `${val.length}/${MAX}` : undefined}
                        sx={isDesc ? { gridColumn: "1 / -1" } : undefined}
                      />
                    );
                  })}
                </Box>
              </Box>
            );
          })}

          {/* Actions */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", gap: 1, mt: 1, alignItems: "center" }}
          >
            {mapId && <Chip label={`GPS ${mapId}`} variant="outlined" />}
            <Box sx={{ flex: 1 }} />
            <Button
              type="submit"
              variant="contained"
              disabled={submitLoading || !user || !mapId.trim()}
              startIcon={submitLoading ? <CircularProgress size={18} /> : null}
            >
              {submitLoading ? "Submitting…" : "Submit"}
            </Button>
            <Button
              type="button"
              variant="text"
              onClick={() => setFields(blankFields(formType))}
            >
              Clear Fields
            </Button>
          </Box>
        </ScrollBox>

        {/* RIGHT COLUMN — Sticky preview on lg+ */}
        <PreviewPanel fields={fields} mapId={mapId} formType={formType} />
      </Box>
    </Paper>
  );
};

export default HorizontalVerticalControlForm;